import React, { useState, useEffect, useMemo } from "react";
import { FaCheck, FaRegEdit, FaRegEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import TableSkeleton from "../../TableSkeleton";

const AllPageTable = ({
  data,
  searchTerm,
  itemsPerPage = 10,
  handleSearch,
  handleEdit,
  openDeleteModal,
  loading,
}) => {
  const [selectedRows, setSelectedRows] = useState(
    new Array(data.length).fill(false)
  );
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // --- BEGIN: handlePageChange defined earlier ---
  // Handle page change - Defined early to ensure accessibility
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) { // totalPages will be defined by useMemo soon
      setCurrentPage(page);
    }
  };
  // --- END: handlePageChange defined earlier ---


  // Skeleton columns configuration
  const skeletonColumns = [
    { width: 40, height: 16, isCircle: false, className: "pl-3" }, // Checkbox
    { width: 80, height: 16, className: "pl-3" }, // Page ID
    { width: 200, height: 16, className: "pl-3" }, // Page Name
    { width: 200, height: 16, className: "pl-3" }, // Slug
    { width: 120, height: 16, className: "pl-3" }, // Actions
  ];

  // --- Move filtered data calculations here, before useEffects that use them ---
  const memoizedFilteredData = useMemo(() => {
    return data.filter((page) => {
      const pageName = page.name ? page.name.toLowerCase() : "";
      const pageSlug = page.slug ? page.slug.toLowerCase() : "";
      const lowerSearchTerm = searchTerm.toLowerCase();

      return (
        pageName.includes(lowerSearchTerm) ||
        pageSlug.includes(lowerSearchTerm)
      );
    });
  }, [data, searchTerm]); // Dependencies for useMemo

  // Calculate pagination indices and current data slice
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = memoizedFilteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(memoizedFilteredData.length / itemsPerPage);
  // --- End of moved calculations ---


  // Reset selected rows and header checkbox when data changes
  useEffect(() => {
    setSelectedRows(new Array(data.length).fill(false));
    setIsHeaderChecked(false);
    setCurrentPage(1); // Reset to first page on new data/search term change
  }, [data]); // Only re-run when the 'data' prop reference changes

  // Update the header checkbox state based on individual checkboxes (re-evaluate)
  useEffect(() => {
    // If there's no data or no items on the current page, header can't be checked
    if (currentData.length === 0) {
      setIsHeaderChecked(false);
      return;
    }
    // Check if all *currently displayed* rows are selected
    const allCurrentRowsSelected = currentData.every((_, index) =>
      selectedRows[indexOfFirstItem + index]
    );
    setIsHeaderChecked(allCurrentRowsSelected);
  }, [selectedRows, currentData, indexOfFirstItem]);


  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows]; // Create a mutable copy
    const areAllCurrentlyChecked = currentData.every((_, index) => selectedRows[indexOfFirstItem + index]);

    currentData.forEach((_, index) => {
        newSelectedRows[indexOfFirstItem + index] = !areAllCurrentlyChecked;
    });
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!areAllCurrentlyChecked);
  };


  const handleRowCheckboxChange = (globalIndex) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[globalIndex] = !newSelectedRows[globalIndex];
    setSelectedRows(newSelectedRows);
    // Recalculate header checkbox state based on *all* selectedRows
    const allInCurrentPageSelected = currentData.every((_, index) => 
        newSelectedRows[indexOfFirstItem + index]
    );
    setIsHeaderChecked(allInCurrentPageSelected);
  };

  // Handle search input change. Pass this directly to the onChange of the input.
  const handleSearchInputChange = (e) => {
    handleSearch(e.target.value);
    setCurrentPage(1); 
  };


  return (
    <div>
      <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3 flex-1">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Pages List
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={() => {
                  const selectedPageIds = data
                    .map((page, index) =>
                      selectedRows[index] ? page.id : null
                    )
                    .filter((id) => id !== null);
                  openDeleteModal(selectedPageIds);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {loading ? (
              <Skeleton height={40} width={192} />
            ) : (
              <input
                type="text"
                placeholder="Search Pages..."
                className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
                value={searchTerm}
                onChange={handleSearchInputChange}
              />
            )}
            <Link
              to="/add-page"
              className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
            >
              + Add Page
            </Link>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="px-3 bg-[#FBFBFD]">
                <th className="text-left">
                  <div className="relative left-3 -top-[3px]">
                    <input
                      type="checkbox"
                      id="header-checkbox"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="header-checkbox"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Page ID
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Page Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Slug
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                />
              ) : (
                currentData.map((page, index) => (
                  <tr
                    key={page.id}
                    className="border-b border-M-text-color/10 hover:bg-gray-100 border-collapse"
                  >
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={"page" + index}
                          className="hidden peer"
                          checked={selectedRows[indexOfFirstItem + index]}
                          onChange={() => handleRowCheckboxChange(indexOfFirstItem + index)}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor={"page" + index}
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                        ></label>
                      </div>
                    </td>
                    <td className="p-3 text-sm">{page.id}</td>
                    <td className="p-3 text-sm">{page.name}</td>
                    <td className="p-3 text-sm">{page.slug}</td>
                    <td className="text-center p-3">
                      <div className="flex justify-start gap-2">
                        <Link
                          to={`https://muktihospital.com/${page.slug}`}
                          className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          title="View"
                        >
                          <FaRegEye />
                        </Link>
                        <button
                          onClick={() => handleEdit(page.id)}
                          className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                          title="Edit"
                        >
                          <FaRegEdit />
                        </button>
                        <button
                          onClick={() => openDeleteModal(page)}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Area */}
        {totalPages > 1 && (
          <div className="p-5 text-end">
            <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
              {/* Previous Button */}
              <li>
                <button
                  className="table-pagination-item disabled:opacity-50"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <MdKeyboardArrowLeft />
                </button>
              </li>

              {/* Pagination Items */}
              {totalPages <= 10
                ? // Show all pages if totalPages <= 10
                  [...Array(totalPages)].map((_, i) => (
                    <li key={i}>
                      <button
                        className={`table-pagination-item ${
                          currentPage === i + 1
                            ? "bg-M-primary-color text-white"
                            : ""
                        }`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))
                : // Show first 5, ellipsis, and last 3 if totalPages > 10
                  (() => {
                    const pages = [];
                    const firstPages = 5;
                    const lastPages = 3;

                    // Always show first 5 pages
                    for (
                      let i = 1;
                      i <= Math.min(firstPages, totalPages);
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    // Add ellipsis if there are pages between first 5 and last 3
                    if (totalPages > firstPages + lastPages) {
                      pages.push(
                        <li key="ellipsis" className="table-pagination-item">
                          ...
                        </li>
                      );
                    }

                    // Show last 3 pages
                    for (
                      let i = Math.max(
                        firstPages + 1,
                        totalPages - lastPages + 1
                      );
                      i <= totalPages;
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    return pages;
                  })()}

              {/* Next Button */}
              <li>
                <button
                  className="table-pagination-item"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <MdKeyboardArrowRight />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllPageTable;